import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronRight } from "lucide-react";

// Google OAuth URL helper
export const getGoogleAuthUrl = () => {
  const redirectUrl = window.location.origin + '/';
  return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

/**
 * Navigation bar for landing page
 */
export function LandingNav({ user }) {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <img src="/logo-new.png" alt="Setuxpress" className="h-10 md:h-12" />
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/browse" className="text-sm font-medium hover:text-primary transition-colors" data-testid="nav-browse">
              Cargo Space
            </Link>
            <Link to="/local-delivery" className="text-sm font-medium hover:text-accent transition-colors" data-testid="nav-local">
              Local Delivery
            </Link>
            <Link to="/private-jets" className="text-sm font-medium hover:text-primary transition-colors" data-testid="nav-jets">
              Private Jets
            </Link>
            <Link to="/carpool" className="text-sm font-medium hover:text-accent transition-colors" data-testid="nav-carpool">
              Carpool
            </Link>
          </div>

          <div className="flex items-center gap-3">
            {user ? (
              <Link to={user.user_type === "partner" ? "/partner" : "/dashboard"}>
                <Button className="rounded-none btn-hover" data-testid="dashboard-btn">
                  Dashboard
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/auth?tab=login">
                  <Button variant="ghost" className="rounded-none" data-testid="login-btn">
                    Sign In
                  </Button>
                </Link>
                <a href={getGoogleAuthUrl()}>
                  <Button className="rounded-none btn-hover bg-accent text-accent-foreground hover:bg-accent/90 flex items-center gap-2" data-testid="google-signin-btn">
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Google
                  </Button>
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

/**
 * Hero section with main CTA
 */
export function HeroSection({ user }) {
  return (
    <section className="pt-16">
      <div className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/3581694/pexels-photo-3581694.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="India logistics hub"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/50" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <p className="label-text mb-4 opacity-0 animate-fade-in-up stagger-1">
              India's First Cargo Space Marketplace
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none mb-6 opacity-0 animate-fade-in-up stagger-2">
              Share Cargo Space.<br />
              <span className="text-accent">Save Costs.</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-lg opacity-0 animate-fade-in-up stagger-3">
              Connect with logistics partners across India. Find available cargo space on road, rail, air, and water routes. Book instantly.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up stagger-4">
              <Link to="/browse">
                <Button size="lg" className="rounded-none btn-hover text-base px-8" data-testid="hero-browse-btn">
                  Browse Available Space
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              {!user && (
                <Link to="/auth?tab=register&role=partner">
                  <Button size="lg" variant="outline" className="rounded-none btn-hover text-base px-8" data-testid="hero-partner-btn">
                    List Your Space
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Stats bar showing platform metrics
 */
export function StatsBar() {
  const stats = [
    { value: "500+", label: "Active Partners" },
    { value: "50K+", label: "Shipments Completed" },
    { value: "100+", label: "Cities Covered" },
    { value: "24/7", label: "Support" },
  ];

  return (
    <section className="border-y border-border bg-secondary/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
          {stats.map((stat) => (
            <div key={stat.label} className="py-8 px-4 text-center">
              <p className="text-3xl font-black tracking-tight">{stat.value}</p>
              <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
